import { useEffect, useState } from 'react';

import { BarChart3, GraduationCap, Image, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';

import {
  CHAVES,
  useConfiguracao,
  type ConfiguracaoDeInscricao,
  type IdentidadeVisual,
  type ScriptsDeRastreamento,
} from './dados';

const MAX_ESCOLAS_LIMITE = 3;

/**
 * `/admin/configuracoes/gerais` — as configurações gerais do sistema,
 * em três seções independentes: identidade visual do portal público, scripts de
 * medição de uso e limites da inscrição de credenciamento.
 *
 * Cada seção tem o próprio "Salvar" porque no protótipo cada uma grava um
 * conjunto de chaves diferente — e mantê-las separadas evita que um erro numa
 * seção impeça de salvar as outras, quando o endpoint existir.
 *
 * NENHUM "SALVAR" GRAVA, E NENHUM UPLOAD ENVIA ARQUIVO. `/configuracoes` não
 * existe na API, a tabela `configuracoes` não tem política de escrita, e não há
 * serviço de arquivos configurado neste repositório. Os botões mostram o aviso
 * honesto de indisponibilidade em vez de fingir sucesso
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 *
 * Os campos de favicon e imagem de compartilhamento aceitam endereço digitado —
 * isso é estado local e funciona. O que não existe é o botão que sobe um arquivo
 * do computador: ele avisa, em vez de abrir um seletor que não levaria a nada.
 */
export function ConfiguracoesGeraisPage() {
  const identidade = useConfiguracao<IdentidadeVisual>(CHAVES.identidadeVisual);
  const rastreamento = useConfiguracao<ScriptsDeRastreamento>(CHAVES.rastreamento);
  const inscricao = useConfiguracao<ConfiguracaoDeInscricao>(CHAVES.inscricao);

  const carregando = identidade.isLoading || rastreamento.isLoading || inscricao.isLoading;
  const comErro = identidade.isError || rastreamento.isError || inscricao.isError;

  function recarregarTudo() {
    void identidade.refetch();
    void rastreamento.refetch();
    void inscricao.refetch();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Configurações gerais"
        description="Identidade visual do portal público, medição de uso e limites da inscrição de credenciamento."
      />

      {comErro ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar as configurações agora. Verifique sua conexão e tente novamente."
          primaryAction={{ label: 'Tentar de novo', onClick: recarregarTudo }}
        />
      ) : carregando ? (
        <Spinner />
      ) : (
        <>
          <SecaoIdentidadeVisual valores={identidade.data} />
          <SecaoRastreamento valores={rastreamento.data} />
          <SecaoInscricao valores={inscricao.data} />
        </>
      )}
    </div>
  );
}

/** Rodapé de seção: um "Salvar" que avisa em vez de gravar. */
function BotaoSalvarSecao({ aoValidar }: { aoValidar?: () => boolean }) {
  function salvar() {
    if (aoValidar && !aoValidar()) {
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="flex justify-end pt-2">
      <Button onClick={salvar}>
        <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
        Salvar
      </Button>
    </div>
  );
}

function SecaoIdentidadeVisual({ valores }: { valores: IdentidadeVisual | undefined }) {
  const [favicon, setFavicon] = useState('');
  const [imagemDeCompartilhamento, setImagemDeCompartilhamento] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    if (valores) {
      setFavicon(valores.favicon_url);
      setImagemDeCompartilhamento(valores.og_image_url);
      setTitulo(valores.site_title);
      setDescricao(valores.site_description);
    }
  }, [valores]);

  /** Não há serviço de arquivos configurado — o botão avisa em vez de abrir o seletor. */
  function enviarArquivo() {
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Image className="h-4 w-4 text-primary" aria-hidden="true" />
          Identidade visual e busca
        </CardTitle>
        <CardDescription>
          Ícone da aba, imagem exibida ao compartilhar o endereço, título e descrição do portal
          público.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="config-favicon">Ícone da aba (favicon)</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="config-favicon"
              value={favicon}
              onChange={(evento) => setFavicon(evento.target.value)}
              placeholder="Endereço da imagem"
            />
            <Button variant="outline" size="sm" onClick={enviarArquivo} className="shrink-0">
              <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Enviar arquivo
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="config-og">Imagem de compartilhamento</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="config-og"
              value={imagemDeCompartilhamento}
              onChange={(evento) => setImagemDeCompartilhamento(evento.target.value)}
              placeholder="Endereço da imagem"
            />
            <Button variant="outline" size="sm" onClick={enviarArquivo} className="shrink-0">
              <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Enviar arquivo
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Aparece quando alguém compartilha o endereço do portal numa rede social ou mensageiro.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="config-titulo">Título do site</Label>
          <Input
            id="config-titulo"
            value={titulo}
            onChange={(evento) => setTitulo(evento.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="config-descricao">Descrição do site</Label>
          <Textarea
            id="config-descricao"
            rows={3}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            É o resumo que aparece nos resultados de busca. Até cerca de 160 caracteres.
          </p>
        </div>

        <BotaoSalvarSecao />
      </CardContent>
    </Card>
  );
}

function SecaoRastreamento({ valores }: { valores: ScriptsDeRastreamento | undefined }) {
  const [googleAnalytics, setGoogleAnalytics] = useState('');
  const [clarity, setClarity] = useState('');

  useEffect(() => {
    if (valores) {
      setGoogleAnalytics(valores.google_analytics_id);
      setClarity(valores.microsoft_clarity_id);
    }
  }, [valores]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
          Medição de uso
        </CardTitle>
        <CardDescription>
          Identificadores das ferramentas que medem o uso do portal público.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="config-ga">Google Analytics — identificador de medição</Label>
          <Input
            id="config-ga"
            value={googleAnalytics}
            onChange={(evento) => setGoogleAnalytics(evento.target.value)}
            placeholder="G-XXXXXXXXXX"
            className="font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="config-clarity">Microsoft Clarity — identificador do projeto</Label>
          <Input
            id="config-clarity"
            value={clarity}
            onChange={(evento) => setClarity(evento.target.value)}
            className="font-mono"
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Deixe em branco para não carregar a ferramenta. O aviso de cookies do portal precisa
          refletir o que estiver ativo aqui.
        </p>

        <BotaoSalvarSecao />
      </CardContent>
    </Card>
  );
}

function SecaoInscricao({ valores }: { valores: ConfiguracaoDeInscricao | undefined }) {
  const [maxEscolas, setMaxEscolas] = useState(MAX_ESCOLAS_LIMITE);

  useEffect(() => {
    if (valores) {
      const limite = Number(valores.max_escolas_por_inscricao);
      setMaxEscolas(
        Number.isFinite(limite) && limite >= 1 && limite <= MAX_ESCOLAS_LIMITE
          ? Math.trunc(limite)
          : MAX_ESCOLAS_LIMITE,
      );
    }
  }, [valores]);

  function validar(): boolean {
    if (!Number.isInteger(maxEscolas) || maxEscolas < 1 || maxEscolas > MAX_ESCOLAS_LIMITE) {
      toast.error('Limite inválido', {
        description: `Informe um número entre 1 e ${MAX_ESCOLAS_LIMITE}.`,
      });
      return false;
    }
    return true;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <GraduationCap className="h-4 w-4 text-primary" aria-hidden="true" />
          Inscrição e credenciamento
        </CardTitle>
        <CardDescription>Limites que valem para a inscrição do transportador.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="config-max-escolas">Máximo de escolas por inscrição</Label>
          <Input
            id="config-max-escolas"
            type="number"
            min={1}
            max={MAX_ESCOLAS_LIMITE}
            value={maxEscolas}
            onChange={(evento) => setMaxEscolas(Number(evento.target.value))}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground">
            Quantas unidades escolares o candidato pode indicar numa mesma inscrição. O teto é{' '}
            {MAX_ESCOLAS_LIMITE}.
          </p>
        </div>

        <BotaoSalvarSecao aoValidar={validar} />
      </CardContent>
    </Card>
  );
}

export default ConfiguracoesGeraisPage;
